import { ArrowRight, ExternalLink, Github } from "lucide-react";
import Footer from "../components/Footer";
import Header from "../components/Header";
import SEO from "../components/SEO";
import { JOIN_FORM_URL } from "../config/links";

interface LabProject {
  name: string;
  domain: string;
  website: string;
  repository: string;
  description: string;
  problem: string;
  status: string;
  statusColor: string;
  facts: Array<{ label: string; value: string }>;
  outputs: Array<{ type: string; label: string; href: string }>;
  questions: string[];
  skills: string[];
  nextPhase?: string;
}

const projects: LabProject[] = [
  {
    name: "useosm",
    domain: "Open mapping and community infrastructure",
    website: "https://www.useosm.org/",
    repository: "https://github.com/unpatterned-labs/useosm",
    description:
      "An open platform that makes the OpenStreetMap ecosystem easier to discover, understand, and use.",
    problem:
      "OpenStreetMap powers work across transport, humanitarian response, urban planning, climate, and location services, but the tools, examples, and routes into the ecosystem remain fragmented. useosm brings those resources into one accessible public platform.",
    status: "Active",
    statusColor: "#2d8659",
    facts: [
      { label: "Supported by", value: "OpenStreetMap Foundation" },
      { label: "License", value: "MIT" },
    ],
    outputs: [
      {
        type: "Platform",
        label: "useosm.org",
        href: "https://www.useosm.org/",
      },
      {
        type: "Repository",
        label: "GitHub",
        href: "https://github.com/unpatterned-labs/useosm",
      },
    ],
    questions: [
      "How can people find the right OpenStreetMap tools for a specific job?",
      "Which real-world use cases best communicate the value of open map data?",
      "How should communities contribute resources, examples, and local knowledge?",
    ],
    skills: [
      "OpenStreetMap",
      "Astro",
      "TypeScript",
      "Data curation",
      "Technical writing",
    ],
  },
  {
    name: "arche",
    domain: "Representation infrastructure and entity tools",
    website: "https://unpatterned-labs.github.io/arche/",
    repository: "https://github.com/unpatterned-labs/arche",
    description:
      "A Python library for the entity layer: making records refer to real people, places, organizations, and products.",
    problem:
      "Identity and entity information is scattered across text, documents, and databases, while the rules for handling it differ by jurisdiction. Arche helps teams detect sensitive data, apply protection policies, and prepare reliable matching signals without rebuilding each layer.",
    status: "Pre-beta",
    statusColor: "#ffcc10",
    facts: [
      { label: "Package", value: "arche-core" },
      { label: "License", value: "Apache 2.0" },
    ],
    outputs: [
      {
        type: "Documentation",
        label: "arche docs",
        href: "https://unpatterned-labs.github.io/arche/",
      },
      {
        type: "Python package",
        label: "arche-core on PyPI",
        href: "https://pypi.org/project/arche-core/",
      },
      {
        type: "Repository",
        label: "GitHub",
        href: "https://github.com/unpatterned-labs/arche",
      },
    ],
    questions: [
      "How can identity data be resolved without exposing raw identifiers?",
      "How should people review, correct, and audit uncertain detections or matches?",
      "How can credentials, registries, and AI workflows point to the right underlying entity?",
      "Which entity workflows need a visual interface rather than Python code?",
    ],
    skills: [
      "Python",
      "Privacy engineering",
      "Entity resolution",
      "NLP and NER",
      "Applied cryptography",
      "Digital public infrastructure",
      "Data protection",
    ],
    nextPhase:
      "Next: a visual workspace for reviewing, protecting, and resolving entity records.",
  },
];

const contributionSteps = [
  {
    title: "Read the work",
    description:
      "Start with the project documentation, current outputs, and open technical questions.",
  },
  {
    title: "Find the useful edge",
    description:
      "Browse issues, test the tools in a real context, or identify a gap in the contributor experience.",
  },
  {
    title: "Make the work legible",
    description:
      "Open an issue or pull request with enough context for another contributor to understand and continue it.",
  },
];

const contributionAreas = [
  "Code and development",
  "Research and analysis",
  "Documentation",
  "Testing and field feedback",
  "Design and developer experience",
  "Project coordination",
];

function ProjectSection({
  project,
  index,
}: {
  project: LabProject;
  index: number;
}) {
  return (
    <article
      className={`border-t border-[#050e1d]/10 px-6 py-20 md:py-28 ${
        index % 2 === 0 ? "bg-[#f7f8fa]" : "bg-white"
      }`}
    >
      <div className="mx-auto grid max-w-6xl gap-10 md:grid-cols-12 md:gap-16">
        <div className="md:col-span-4">
          <p className="font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.18em] text-[#184b9d]">
            Project {String(index + 1).padStart(2, "0")}
          </p>
          <div className="mt-5 flex items-center gap-3">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: project.statusColor }}
              aria-hidden="true"
            />
            <span className="font-['Manrope',sans-serif] text-sm font-semibold text-[#050e1d]">
              {project.status}
            </span>
          </div>
        </div>

        <div className="md:col-span-8">
          <div className="flex flex-col gap-5 border-b border-[#050e1d]/15 pb-10 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.14em] text-[#050e1d]/45">
                {project.domain}
              </p>
              <h2 className="mt-4 font-['DM_Serif_Display',serif] text-5xl leading-tight text-[#050e1d] md:text-6xl">
                {project.name}
              </h2>
              <p className="mt-5 max-w-2xl font-['Manrope',sans-serif] text-lg leading-relaxed text-[#050e1d]/65">
                {project.description}
              </p>
            </div>
            <a
              href={project.website}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${project.name} website`}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#050e1d]/15 text-[#050e1d] transition-colors hover:border-[#184b9d] hover:text-[#184b9d]"
            >
              <ExternalLink size={18} aria-hidden="true" />
            </a>
          </div>

          <div className="grid gap-10 py-10 md:grid-cols-2 md:gap-14">
            <div>
              <h3 className="font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.16em] text-[#050e1d]/40">
                The problem
              </h3>
              <p className="mt-4 font-['Manrope',sans-serif] text-base leading-relaxed text-[#050e1d]/70">
                {project.problem}
              </p>

              <dl className="mt-8 space-y-4 border-t border-[#050e1d]/10 pt-6">
                {project.facts.map((fact) => (
                  <div
                    key={fact.label}
                    className="grid grid-cols-[110px_1fr] gap-4"
                  >
                    <dt className="font-['Manrope',sans-serif] text-sm text-[#050e1d]/45">
                      {fact.label}
                    </dt>
                    <dd className="font-['Manrope',sans-serif] text-sm font-semibold text-[#050e1d]">
                      {fact.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            <div>
              <h3 className="font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.16em] text-[#050e1d]/40">
                Public outputs
              </h3>
              <div className="mt-4 border-t border-[#050e1d]/10">
                {project.outputs.map((output) => (
                  <a
                    key={`${output.type}-${output.label}`}
                    href={output.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-between gap-4 border-b border-[#050e1d]/10 py-4"
                  >
                    <span>
                      <span className="block font-['Manrope',sans-serif] text-xs text-[#050e1d]/40">
                        {output.type}
                      </span>
                      <span className="mt-1 block font-['Manrope',sans-serif] text-sm font-semibold text-[#050e1d] transition-colors group-hover:text-[#184b9d]">
                        {output.label}
                      </span>
                    </span>
                    <ExternalLink
                      size={15}
                      className="text-[#050e1d]/35 transition-colors group-hover:text-[#184b9d]"
                      aria-hidden="true"
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="grid gap-10 border-t border-[#050e1d]/15 pt-10 md:grid-cols-2 md:gap-14">
            <div>
              <h3 className="font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.16em] text-[#050e1d]/40">
                Open questions
              </h3>
              <ol className="mt-5 space-y-4">
                {project.questions.map((question, questionIndex) => (
                  <li
                    key={question}
                    className="grid grid-cols-[32px_1fr] gap-3 font-['Manrope',sans-serif] text-sm leading-relaxed text-[#050e1d]/65"
                  >
                    <span className="font-mono text-xs text-[#184b9d]/55">
                      {String(questionIndex + 1).padStart(2, "0")}
                    </span>
                    {question}
                  </li>
                ))}
              </ol>
            </div>

            <div>
              <h3 className="font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.16em] text-[#050e1d]/40">
                Useful experience
              </h3>
              <div className="mt-5 flex flex-wrap gap-x-5 gap-y-3">
                {project.skills.map((skill) => (
                  <span
                    key={skill}
                    className="font-['Manrope',sans-serif] text-sm font-medium text-[#050e1d]/70"
                  >
                    {skill}
                  </span>
                ))}
              </div>
              <a
                href={project.repository}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 font-['Manrope',sans-serif] text-sm font-semibold text-[#184b9d] transition-colors hover:text-[#050e1d]"
              >
                View the repository
                <ArrowRight size={15} aria-hidden="true" />
              </a>
            </div>
          </div>

          {project.nextPhase && (
            <div className="mt-10 border-l-2 border-[#184b9d] pl-6">
              <p className="font-['Manrope',sans-serif] text-sm leading-relaxed text-[#050e1d]/65">
                {project.nextPhase}
              </p>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

export default function Labs() {
  const publicOutputCount = projects.reduce(
    (total, project) => total + project.outputs.length,
    0,
  );

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Labs | Unpatterned"
        description="Open-source tools and prototypes from Unpatterned Labs, including useosm and Arche."
      />
      <Header />

      <main>
        <section className="px-6 pb-20 pt-32 md:pb-28 md:pt-40">
          <div className="mx-auto max-w-6xl">
            <div className="flex items-center gap-4 font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.18em] text-[#184b9d]">
              <span>Unpatterned Labs</span>
              <span className="h-px w-12 bg-[#184b9d]/30" aria-hidden="true" />
              <span>Work in public</span>
            </div>

            <h1 className="mt-8 max-w-5xl font-['DM_Serif_Display',serif] text-5xl leading-[1.02] text-[#050e1d] sm:text-6xl md:text-7xl">
              Where research questions become working software.
            </h1>

            <div className="mt-12 grid gap-8 border-t border-[#050e1d]/15 pt-8 md:grid-cols-12">
              <p className="font-['DM_Serif_Display',serif] text-2xl leading-snug text-[#050e1d] md:col-span-5 md:text-3xl">
                Labs is the shipping arm of Unpatterned: open-source tools,
                prototypes, and applied pilots.
              </p>
              <div className="md:col-span-6 md:col-start-7">
                <p className="font-['Manrope',sans-serif] text-lg leading-relaxed text-[#050e1d]/65 md:text-xl">
                  Projects begin with a real infrastructure or knowledge gap.
                  Code, documentation, open questions, and contribution paths
                  stay public from the start.
                </p>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <a
                    href="#projects"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-[#050e1d] px-6 py-3.5 font-['Manrope',sans-serif] font-semibold text-white transition-colors hover:bg-[#184b9d]"
                  >
                    View active work
                    <ArrowRight size={16} aria-hidden="true" />
                  </a>
                  <a
                    href="https://github.com/unpatterned-labs"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-[#050e1d]/20 px-6 py-3.5 font-['Manrope',sans-serif] font-semibold text-[#050e1d] transition-colors hover:border-[#050e1d]"
                  >
                    <Github size={17} aria-hidden="true" />
                    GitHub
                  </a>
                </div>
              </div>
            </div>

            <dl className="mt-14 grid border-y border-[#050e1d]/10 sm:grid-cols-3">
              {[
                [
                  "Active projects",
                  String(projects.length).padStart(2, "0"),
                ],
                [
                  "Public outputs",
                  String(publicOutputCount).padStart(2, "0"),
                ],
                ["Open licenses", "02"],
              ].map(([label, value], index) => (
                <div
                  key={label}
                  className={`flex items-baseline justify-between gap-4 py-4 ${
                    index > 0
                      ? "border-t border-[#050e1d]/10 sm:border-l sm:border-t-0 sm:pl-6"
                      : ""
                  }`}
                >
                  <dt className="font-['Manrope',sans-serif] text-sm text-[#050e1d]/55">
                    {label}
                  </dt>
                  <dd className="font-mono text-sm text-[#184b9d]">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section id="projects" aria-label="Active projects">
          {projects.map((project, index) => (
            <ProjectSection
              key={project.name}
              project={project}
              index={index}
            />
          ))}
        </section>

        <section id="contribute" className="px-6 py-20 md:py-28">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-10 md:grid-cols-12 md:gap-16">
              <div className="md:col-span-4">
                <p className="font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.18em] text-[#184b9d]">
                  Contribute
                </p>
                <h2 className="mt-5 font-['DM_Serif_Display',serif] text-4xl leading-tight text-[#050e1d] md:text-5xl">
                  Start with a useful piece of work.
                </h2>
                <p className="mt-5 font-['Manrope',sans-serif] text-base leading-relaxed text-[#050e1d]/60">
                  We value clear thinking and follow-through over credentials.
                  Small, well-explained contributions compound.
                </p>
              </div>

              <div className="border-t border-[#050e1d]/15 md:col-span-7 md:col-start-6">
                {contributionSteps.map((step, index) => (
                  <article
                    key={step.title}
                    className="grid gap-3 border-b border-[#050e1d]/15 py-7 sm:grid-cols-[44px_1fr]"
                  >
                    <span className="pt-1 font-mono text-xs text-[#184b9d]/55">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="font-['Manrope',sans-serif] text-lg font-semibold text-[#050e1d]">
                        {step.title}
                      </h3>
                      <p className="mt-2 font-['Manrope',sans-serif] text-base leading-relaxed text-[#050e1d]/60">
                        {step.description}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <div className="mt-16 grid gap-8 border-t border-[#050e1d]/15 pt-8 md:grid-cols-12">
              <h3 className="font-['DM_Serif_Display',serif] text-3xl text-[#050e1d] md:col-span-4">
                Ways to help
              </h3>
              <ul className="grid gap-x-10 gap-y-4 sm:grid-cols-2 md:col-span-7 md:col-start-6">
                {contributionAreas.map((area, index) => (
                  <li
                    key={area}
                    className="flex items-baseline gap-3 font-['Manrope',sans-serif] text-sm font-medium text-[#050e1d]/70"
                  >
                    <span className="font-mono text-xs text-[#184b9d]/50">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    {area}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="bg-[#f2f6fc] px-6 py-20 md:py-24">
          <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-12">
            <h2 className="font-['DM_Serif_Display',serif] text-4xl leading-tight text-[#050e1d] md:col-span-7 md:text-5xl">
              Bring a problem worth understanding deeply.
            </h2>
            <div className="md:col-span-5 md:self-end">
              <p className="font-['Manrope',sans-serif] text-base leading-relaxed text-[#050e1d]/60">
                Join the community to contribute to an existing project or
                discuss infrastructure gaps you are seeing in practice.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <a
                  href={JOIN_FORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-md bg-[#184b9d] px-6 py-3.5 font-['Manrope',sans-serif] font-semibold text-white transition-colors hover:bg-[#050e1d]"
                >
                  Join the community
                </a>
                <a
                  href="https://github.com/unpatterned-labs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-md border border-[#050e1d]/20 px-6 py-3.5 font-['Manrope',sans-serif] font-semibold text-[#050e1d] transition-colors hover:border-[#050e1d]"
                >
                  Browse GitHub
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
